/**
 * Structures tab — templates that insert as compound tokens.
 *
 * Layout sections: Most common · Powers & roots · Multi-line · Matrices.
 * Tiles wrap to fit the drawer width; "big" tiles span the full row.
 *
 * No horizontal scroll under any width — the grid is
 * `repeat(auto-fit, minmax(120px, 1fr))` with `overflow-x: hidden`.
 */
import { useRef, useState } from "react";
import { renderStructure } from "./structureGlyphs";
import { T, type StructToken, type Token } from "./tokens";

interface Tile {
  name: string;
  cmd: string;
  /** When true, tile spans the full row width. */
  big?: boolean;
  /** Optional shortcut chip in the top-right corner. */
  shortcut?: string;
  /** Builds the token to insert (also drives the tile's preview glyph). */
  make: () => Token;
  /**
   * Script action instead of a plain insert: bind a super/subscript to
   * the atom before the caret (the button equivalent of pressing `^`/`_`,
   * which is awkward on some keyboard layouts). `make` still supplies the
   * preview glyph.
   */
  action?: "sup" | "sub";
}

interface Section {
  label: string;
  tiles: Tile[];
}

function buildSections(): Section[] {
  return [
    {
      label: "Most common",
      tiles: [
        { name: "Fraction",         cmd: "\\frac{a}{b}", shortcut: "/", make: () => T.frac() },
        { name: "Definite integral", cmd: "\\int_a^b",   shortcut: "∫", make: () => T.integral() },
        { name: "Summation",        cmd: "\\sum",        shortcut: "∑", make: () => T.sumStruct() },
        { name: "Limit",            cmd: "\\lim",        shortcut: "L", make: () => T.limStruct() },
      ],
    },
    {
      label: "Powers & roots",
      tiles: [
        // Superscript / subscript script the atom before the caret — the
        // button equivalent of `^` / `_` (no keyboard layout gymnastics).
        { name: "Superscript", cmd: "xⁿ", shortcut: "^", action: "sup", make: () => T.subsup("x", "", "n") },
        { name: "Subscript",   cmd: "xₙ", shortcut: "_", action: "sub", make: () => T.subsup("x", "n", "") },
        { name: "Square root",    cmd: "\\sqrt{x}",       shortcut: "r",   make: () => T.sqrt() },
        { name: "nth root",       cmd: "\\sqrt[n]{x}",                    make: () => T.nthroot() },
        { name: "Sub & sup",      cmd: "x_i^n",                           make: () => T.subsup() },
        { name: "Binomial",       cmd: "\\binom{n}{k}",                   make: () => T.binom() },
      ],
    },
    {
      label: "Delimiters",
      tiles: [
        // Parentheses / brackets / |·| can't be typed (no key + not in the
        // symbol palette), so they're only reachable as these tiles.
        { name: "Parentheses",    cmd: "( )",  make: () => T.parens() },
        { name: "Brackets",       cmd: "[ ]",  make: () => T.parens([], "bracket") },
        { name: "Absolute value", cmd: "|x|",  make: () => T.abs() },
      ],
    },
    {
      label: "Accents",
      tiles: [
        { name: "Hat",   cmd: "\\hat{x}",   make: () => T.accent("hat") },
        { name: "Vec",   cmd: "\\vec{x}",   make: () => T.accent("vec") },
        { name: "Bar",   cmd: "\\bar{x}",   make: () => T.accent("bar") },
        { name: "Tilde", cmd: "\\tilde{x}", make: () => T.accent("tilde") },
        { name: "Dot",   cmd: "\\dot{x}",   make: () => T.accent("dot") },
      ],
    },
    {
      label: "Multi-line",
      tiles: [
        { name: "Aligned equations", cmd: "\\begin{aligned}…", big: true, make: () => T.aligned() },
        { name: "Cases",             cmd: "\\begin{cases}…",   big: true, make: () => T.cases() },
        { name: "New line",          cmd: "\\\\",   shortcut: "⇧↵", make: () => T.newline() },
        { name: "Align point",       cmd: "&",      shortcut: "&",  make: () => T.align() },
      ],
    },
    {
      label: "Matrices & vectors",
      tiles: [
        { name: "2×2 matrix",      cmd: "\\begin{pmatrix}…", make: () => T.matrix(2, 2) },
        { name: "3×3 matrix",      cmd: "\\begin{pmatrix}…", make: () => T.matrix(3, 3) },
        { name: "4×4 matrix",      cmd: "\\begin{pmatrix}…", make: () => T.matrix(4, 4) },
        { name: "Column vector",   cmd: "\\begin{pmatrix}…", make: () => T.vector() },
      ],
    },
  ];
}

interface StructuresTabProps {
  onInsert: (token: Token) => void;
  /** Script the preceding atom (sup/sub button — the `^`/`_` equivalent). */
  onScript?: (which: "sup" | "sub") => void;
  showShortcuts: boolean;
}

export function StructuresTab({ onInsert, onScript, showShortcuts }: StructuresTabProps) {
  const sections = buildSections();
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Indigo banner */}
      <div
        style={{
          padding: "8px 12px",
          margin: 12,
          marginBottom: 0,
          borderRadius: 6,
          background: "color-mix(in srgb, var(--cat-struct) 8%, transparent)",
          color: "var(--lilia-text-secondary)",
          fontSize: 11.5,
          lineHeight: 1.5,
          borderLeft: "3px solid var(--cat-struct)",
        }}
      >
        <strong style={{ color: "var(--lilia-text)" }}>Templates</strong> insert with placeholder slots. Press <KbdMini>Tab</KbdMini> to cycle slots and type to fill.
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: 12 }}>
        {sections.map((sec) => (
          <div key={sec.label} style={{ marginBottom: 14 }}>
            <div
              style={{
                fontFamily: "var(--lilia-font-sans, system-ui, sans-serif)",
                fontSize: 9.5, fontWeight: 700,
                color: "var(--lilia-text-muted)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                paddingBottom: 6,
                marginBottom: 6,
                borderBottom: "1px solid var(--lilia-border)",
              }}
            >
              {sec.label}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 6,
              }}
            >
              {sec.tiles.map((tile) => (
                <StructureTile
                  key={tile.name}
                  tile={tile}
                  showShortcuts={showShortcuts}
                  onInsert={() => {
                    if (tile.action && onScript) onScript(tile.action);
                    else onInsert(tile.make());
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StructureTile({
  tile, showShortcuts, onInsert,
}: {
  tile: Tile;
  showShortcuts: boolean;
  onInsert: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  // Post-click "key press" flash — a brief, strong highlight that stays
  // visible for ~450ms after the click so it shows up in the recorded
  // build video + the per-step capture screenshots (calculator feel).
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<number | undefined>(undefined);
  const handleClick = () => {
    setFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(false), 450);
    onInsert();
  };
  // Preview = the tile's own make() rendered statically (no ctx), so each
  // tile shows its real shape — xⁿ vs xₙ, a 3×3 grid, ( ) vs [ ], etc.
  const preview = renderStructure(tile.make() as StructToken);
  return (
    <button
      type="button"
      data-testid="math-structure-tile"
      data-structure={tile.name}
      data-pressed={flash ? "true" : undefined}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        position: "relative",
        gridColumn: tile.big ? "1 / -1" : "auto",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 8,
        padding: "12px 8px 8px",
        minHeight: 88,
        background: flash
          ? "color-mix(in srgb, var(--lilia-primary) 22%, transparent)"
          : hovered ? "color-mix(in srgb, var(--cat-struct) 8%, transparent)" : "var(--lilia-surface)",
        border: `1px solid ${flash ? "color-mix(in srgb, var(--lilia-primary) 65%, transparent)" : hovered ? "color-mix(in srgb, var(--cat-struct) 23%, transparent)" : "var(--lilia-border)"}`,
        boxShadow: flash ? "0 0 0 3px color-mix(in srgb, var(--lilia-primary) 35%, transparent)" : "none",
        borderRadius: 7,
        cursor: "pointer",
        transition: "background 120ms, border-color 120ms, box-shadow 120ms, transform 80ms",
        transform: pressed ? "scale(0.985)" : flash ? "scale(1.03)" : "none",
      }}
    >
      {showShortcuts && tile.shortcut && (
        <span
          style={{
            position: "absolute",
            top: 6, right: 6,
            padding: "0 4px",
            fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
            fontSize: 9, fontWeight: 600,
            background: "var(--lilia-bg-muted)",
            border: "1px solid var(--lilia-border)",
            borderRadius: 3,
            color: "var(--lilia-text-secondary)",
          }}
        >
          {tile.shortcut}
        </span>
      )}

      <div style={{
        flex: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--lilia-font-serif, 'IBM Plex Serif', Georgia, serif)",
        fontSize: 18, color: "var(--lilia-text)",
      }}>
        {preview}
      </div>

      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--lilia-text)" }}>{tile.name}</span>
        <span style={{
          fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
          fontSize: 9,
          color: "var(--lilia-text-muted)",
        }}>{tile.cmd}</span>
      </div>
    </button>
  );
}

function KbdMini({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
      fontSize: 10, fontWeight: 600,
      background: "var(--lilia-surface)",
      border: "1px solid var(--lilia-border)",
      padding: "0 4px", borderRadius: 3,
      color: "var(--lilia-text-secondary)",
    }}>{children}</span>
  );
}
